import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vansAPI, warehouseAPI } from '../lib/api';
import { checkPlanLimit, handleApiError, showPlanLimitToast, getShortLimitMessage } from '../lib/apiErrors';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AppDrawer } from '../components/ui/AppDrawer';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Plus, Truck, ChevronRight, Loader2, Archive, ArchiveRestore, AlertTriangle, Users, Power, PowerOff, Package, DollarSign, TrendingUp } from 'lucide-react';
import { MobileFAB } from '../components/MobileFAB';
import { CreateVehicleDialog } from '../components/CreateVehicleDialog';
import { toast } from 'sonner';

export const Vans = () => {
  const navigate = useNavigate();
  const { isOwner, permissions } = useAuth();
  const [vans, setVans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Plan limits state
  const [planLimits, setPlanLimits] = useState(null);
  const [planLimitsLoading, setPlanLimitsLoading] = useState(true); // P0 FIX: Track loading to prevent button flash
  
  // Tab state: 'active' | 'inactive' | 'archived'
  const [activeTab, setActiveTab] = useState('active');
  
  // Check if user can manage billing (for showing Upgrade CTA)
  // permissions is an object with permission keys as properties
  const canManageBilling = isOwner || permissions?.['billing.manage'] === true;
  
  // Archive modal state
  const [archiveTarget, setArchiveTarget] = useState(null); // { van, action: 'archive'|'restore' }
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveCheck, setArchiveCheck] = useState(null); // Result of archive-check API
  const [archiveCheckLoading, setArchiveCheckLoading] = useState(false);
  
  // Technician action state for archive
  const [techAction, setTechAction] = useState('unassign_all');
  const [targetVehicleId, setTargetVehicleId] = useState('');
  
  // Toggle active state loading
  const [togglingId, setTogglingId] = useState(null);

  const fetchVans = async () => {
    // P0 FIX: Set plan limits loading at start to prevent button flash
    setPlanLimitsLoading(true);
    
    try {
      // Use backend filtering based on tab
      let params = {};
      if (activeTab === 'archived') {
        params = { archived: true };
      } else if (activeTab === 'inactive') {
        // Get all non-archived vehicles, then filter to inactive on client
        // (backend returns active-only by default, so we need include_inactive)
        params = { include_inactive: true };
      }
      // activeTab === 'active' uses default (active-only, non-archived)
      
      const response = await vansAPI.list(params);
      
      // For inactive tab, filter to only inactive vehicles
      let filteredVans = response.data;
      if (activeTab === 'inactive') {
        filteredVans = response.data.filter(v => !v.is_active && !v.archived_at);
      }
      setVans(filteredVans);
      
      // Fetch plan limits for vehicle creation check
      try {
        const limitsRes = await warehouseAPI.getPlanLimits();
        setPlanLimits(limitsRes.data);
      } catch (limitErr) {
        // Non-fatal - just log it
        console.warn('Failed to fetch plan limits:', limitErr);
      }
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
      setPlanLimitsLoading(false); // P0 FIX: Clear loading state
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchVans();
  }, [activeTab]);

  // Navigate to billing page for upgrade
  const handleUpgradeClick = () => {
    navigate('/billing');
  };

  // Pre-check and open Add Vehicle dialog
  const handleAddVehicleClick = () => {
    const { canCreate, message } = checkPlanLimit(planLimits, 'vehicle');
    
    if (!canCreate) {
      showPlanLimitToast(message, 'vehicle', {
        canUpgrade: canManageBilling,
        onUpgrade: handleUpgradeClick
      });
      return;
    }
    
    setDialogOpen(true);
  };

  // Get tooltip message for disabled button
  const vehicleLimitTooltip = getShortLimitMessage(planLimits, 'vehicle');
  
  // P0 FIX: Button disable logic - ONLY based on:
  // 1. planLimitsLoading (show loading state while fetching)
  // 2. planLimits.vehicle.can_create === false (API says limit reached)
  // Do NOT use any cached tenant data or local assumptions
  const isAtVehicleLimit = !planLimitsLoading && planLimits?.vehicle?.can_create === false;

  const handleSubmit = async (newVehicle) => {
    // Called by CreateVehicleDialog on success
    setDialogOpen(false);
    fetchVans();
  };
  
  // Archive check handler - called when user clicks archive on a vehicle
  const handleArchiveClick = async (van, e) => {
    e.preventDefault();
    e.stopPropagation();
    setArchiveTarget({ van, action: 'archive' });
    setArchiveCheckLoading(true);
    setArchiveCheck(null);
    setTechAction('unassign_all');
    setTargetVehicleId('');
    
    try {
      const response = await vansAPI.checkArchive(van.id);
      setArchiveCheck(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check archive status');
      setArchiveTarget(null);
    } finally {
      setArchiveCheckLoading(false);
    }
  };
  
  // Restore handler
  const handleRestoreClick = (van, e) => {
    e.preventDefault();
    e.stopPropagation();
    setArchiveTarget({ van, action: 'restore' });
    setArchiveCheck(null);
  };
  
  // Archive confirm handler
  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiveLoading(true);
    
    try {
      const payload = archiveCheck?.assigned_technicians?.length > 0 
        ? { 
            action: techAction,
            target_vehicle_id: techAction === 'reassign' ? targetVehicleId : null
          }
        : null;
      
      await vansAPI.archive(archiveTarget.van.id, payload);
      toast.success(`${archiveTarget.van.name} has been archived`);
      setArchiveTarget(null);
      setArchiveCheck(null);
      fetchVans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive vehicle');
    } finally {
      setArchiveLoading(false);
    }
  };
  
  // Restore confirm handler
  const handleRestore = async () => {
    if (!archiveTarget) return;
    setArchiveLoading(true);
    
    try {
      await vansAPI.restore(archiveTarget.van.id);
      toast.success(`${archiveTarget.van.name} has been restored`);
      setArchiveTarget(null);
      fetchVans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore vehicle');
    } finally {
      setArchiveLoading(false);
    }
  };
  
  const closeArchiveDialog = () => {
    setArchiveTarget(null);
    setArchiveCheck(null);
    setTechAction('unassign_all');
    setTargetVehicleId('');
  };
  
  // Get list of active (non-archived) vans for reassignment dropdown
  const activeVansForReassign = vans.filter(v => !v.archived_at && v.id !== archiveTarget?.van?.id);
  
  // Toggle active/inactive state
  const handleToggleActive = async (van, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setTogglingId(van.id);
    try {
      await vansAPI.update(van.id, { is_active: !van.is_active });
      toast.success(van.is_active 
        ? `${van.name} has been deactivated` 
        : `${van.name} has been activated`
      );
      fetchVans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vehicle status');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground">Fleet</h1>
            <p className="text-muted-foreground mt-1 text-sm md:text-base">Manage your service vehicles</p>
          </div>
          
          {/* Desktop Add Vehicle Button (only on Active tab) */}
          {isOwner && activeTab === 'active' && (
            <>
              {planLimitsLoading ? (
                // P0 FIX: Show loading button while fetching plan limits (prevents flash)
                <Button 
                  className="hidden md:inline-flex h-12 px-4 font-bold" 
                  data-testid="add-van-btn"
                  disabled
                  variant="outline"
                >
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Add Vehicle
                </Button>
              ) : isAtVehicleLimit ? (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        className="hidden md:inline-flex h-12 px-4 font-bold" 
                        data-testid="add-van-btn"
                        disabled
                        variant="outline"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Vehicle
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent 
                      className="flex flex-col gap-1"
                      onPointerDownOutside={(e) => e.preventDefault()}
                    >
                      <p>{vehicleLimitTooltip}</p>
                      {canManageBilling && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/billing');
                          }}
                          className="text-xs text-sky-300 underline hover:text-sky-200 cursor-pointer text-left"
                        >
                          Go to Billing
                        </button>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button 
                  className="hidden md:inline-flex h-12 px-4 font-bold btn-press" 
                  data-testid="add-van-btn"
                  onClick={handleAddVehicleClick}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Vehicle
                </Button>
              )}
            </>
          )}
        </div>
        
        {/* Active/Inactive/Archived Tabs */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'active' 
                ? 'text-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-active-vans"
          >
            Active
            {activeTab === 'active' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('inactive')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'inactive' 
                ? 'text-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-inactive-vans"
          >
            Inactive
            {activeTab === 'inactive' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'archived' 
                ? 'text-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-archived-vans"
          >
            Archived
            {activeTab === 'archived' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        </div>

        {/* Fleet Inventory Summary — active tab only, only when vehicles have inventory */}
        {activeTab === 'active' && !loading && vans.length > 0 && (() => {
          const totals = vans.reduce((acc, v) => ({
            items: acc.items + (v.item_count ?? 0),
            cost: acc.cost + (v.inventory_cost ?? 0),
            value: acc.value + (v.inventory_value ?? 0),
            margin: acc.margin + (v.potential_margin ?? 0),
          }), { items: 0, cost: 0, value: 0, margin: 0 });
          if (totals.items === 0) return null;
          const fmt = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fleet-inventory-summary">
              <Card className="relative overflow-hidden" data-testid="fleet-total-items">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
                <CardContent className="p-4 pl-5">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Total Items</p>
                      <p className="text-2xl font-bold mt-1 tabular-nums">{totals.items.toLocaleString()}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-blue-500/10 shrink-0">
                      <Package className="w-5 h-5 text-blue-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="relative overflow-hidden" data-testid="fleet-total-cost">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />
                <CardContent className="p-4 pl-5">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Total Cost</p>
                      <p className="text-2xl font-bold mt-1 tabular-nums">${fmt(totals.cost)}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-amber-500/10 shrink-0">
                      <DollarSign className="w-5 h-5 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="relative overflow-hidden" data-testid="fleet-total-value">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500" />
                <CardContent className="p-4 pl-5">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Total Value</p>
                      <p className="text-2xl font-bold mt-1 tabular-nums">${fmt(totals.value)}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-purple-500/10 shrink-0">
                      <DollarSign className="w-5 h-5 text-purple-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="relative overflow-hidden" data-testid="fleet-total-margin">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" />
                <CardContent className="p-4 pl-5">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Potential Parts Margin</p>
                      <p className={`text-2xl font-bold mt-1 tabular-nums ${totals.margin > 0 ? 'text-emerald-600' : ''}`}>${fmt(totals.margin)}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-emerald-500/10 shrink-0">
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* Fleet List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : vans.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              {activeTab === 'archived' ? (
                <>
                  <Archive className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-semibold text-foreground">No archived vehicles</p>
                  <p className="text-muted-foreground mt-1 text-sm">Archived vehicles will appear here</p>
                </>
              ) : activeTab === 'inactive' ? (
                <>
                  <PowerOff className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-semibold text-foreground">No inactive vehicles</p>
                  <p className="text-muted-foreground mt-1 text-sm">Deactivated vehicles will appear here</p>
                </>
              ) : (
                <>
                  <Truck className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-semibold text-foreground">No vehicles yet</p>
                  <p className="text-muted-foreground mt-1 text-sm">Add your trucks to begin assigning inventory.</p>
                  {isOwner && !planLimitsLoading && !isAtVehicleLimit && (
                    <Button className="mt-4" onClick={handleAddVehicleClick} data-testid="empty-add-vehicle-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Vehicle
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {vans.map((van) => (
                  <div
                    key={van.id}
                    className={`px-4 py-3 cursor-pointer transition-colors hover:bg-muted/40 ${
                      van.archived_at ? 'opacity-60' : ''
                    } ${!van.is_active && !van.archived_at ? 'opacity-60 bg-muted/20' : ''}`}
                    onClick={() => {
                      if (!van.archived_at && van.is_active) navigate(`/vans/${van.id}`);
                    }}
                    data-testid={`van-row-${van.id}`}
                  >
                    <div className="flex items-center gap-3">
                    {/* Left: Vehicle info */}
                    <div className="min-w-0 flex-1">
                      {/* Line 1: Name + badges */}
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={`text-base font-semibold truncate ${
                          !van.is_active && !van.archived_at ? 'text-muted-foreground' : 'text-foreground'
                        }`}>
                          {van.name}
                        </span>
                        {van.archived_at && (
                          <span className="text-xs font-medium px-1.5 py-0 rounded bg-amber-100 text-amber-700">
                            Archived
                          </span>
                        )}
                        {!van.archived_at && !van.is_active && (
                          <span className="text-xs font-medium px-1.5 py-0 rounded bg-muted text-muted-foreground">
                            Inactive
                          </span>
                        )}
                      </div>
                      {/* Line 2: Secondary info */}
                      <div className="text-sm text-muted-foreground mt-0.5">
                        {van.van_number && <span>#{van.van_number}</span>}
                        {van.van_number && van.license_plate && <span> · </span>}
                        {van.license_plate && <span>{van.license_plate}</span>}
                        {!van.van_number && !van.license_plate && (
                          <span className="italic">No ID assigned</span>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions + chevron */}
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Activate/Deactivate toggle (owner-only, non-archived) */}
                      {isOwner && !van.archived_at && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-8 w-8 ${van.is_active
                            ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                            : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                          }`}
                          onClick={(e) => handleToggleActive(van, e)}
                          disabled={togglingId === van.id}
                          title={van.is_active ? 'Deactivate Vehicle' : 'Activate Vehicle'}
                          data-testid={`toggle-active-${van.id}`}
                        >
                          {togglingId === van.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : van.is_active ? (
                            <PowerOff className="w-4 h-4" />
                          ) : (
                            <Power className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {/* Archive/Restore buttons (owner-only) */}
                      {isOwner && (
                        van.archived_at ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={(e) => { e.stopPropagation(); handleRestoreClick(van, e); }}
                            title="Restore Vehicle"
                            data-testid={`restore-van-${van.id}`}
                          >
                            <ArchiveRestore className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={(e) => { e.stopPropagation(); handleArchiveClick(van, e); }}
                            title="Archive Vehicle"
                            data-testid={`archive-van-${van.id}`}
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )
                      )}
                      {!van.archived_at && van.is_active && (
                        <ChevronRight className="w-5 h-5 text-muted-foreground ml-1" />
                      )}
                    </div>
                    </div>
                    {/* Inventory metrics strip */}
                    {!van.archived_at && (
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap" data-testid={`van-metrics-${van.id}`}>
                        <span className="flex items-center gap-1" data-testid={`van-item-count-${van.id}`}>
                          <Package className="w-3 h-3" />
                          {van.item_count ?? 0} items
                        </span>
                        <span className="flex items-center gap-1" data-testid={`van-inv-cost-${van.id}`}>
                          <DollarSign className="w-3 h-3" />
                          Cost: ${(van.inventory_cost ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="flex items-center gap-1" data-testid={`van-inv-value-${van.id}`}>
                          <DollarSign className="w-3 h-3" />
                          Value: ${(van.inventory_value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className={`flex items-center gap-1 font-medium ${(van.potential_margin ?? 0) > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`} data-testid={`van-margin-${van.id}`}>
                          <TrendingUp className="w-3 h-3" />
                          Potential Margin: ${(van.potential_margin ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Mobile FAB — standardized */}
      {isOwner && activeTab === 'active' && !planLimitsLoading && !isAtVehicleLimit && (
        <MobileFAB onClick={handleAddVehicleClick} data-testid="mobile-fab-vehicle" />
      )}

      {/* Add Vehicle Dialog (shared component) */}
      <CreateVehicleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={handleSubmit}
      />

      {/* Archive/Restore Confirmation Dialog */}
      <Dialog open={!!archiveTarget} onOpenChange={(open) => !open && closeArchiveDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {archiveTarget?.action === 'archive' ? (
                <>
                  <Archive className="w-5 h-5 text-amber-600" />
                  Archive Vehicle
                </>
              ) : (
                <>
                  <ArchiveRestore className="w-5 h-5 text-green-600" />
                  Restore Vehicle
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {archiveCheckLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : archiveTarget?.action === 'restore' ? (
            // Restore confirmation
            <div className="space-y-4">
              <DialogDescription>
                Are you sure you want to restore &quot;{archiveTarget?.van?.name}&quot;? It will be visible in the active fleet again.
              </DialogDescription>
              <DialogFooter>
                <Button variant="outline" onClick={closeArchiveDialog} disabled={archiveLoading}>
                  Cancel
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleRestore}
                  disabled={archiveLoading}
                  data-testid="confirm-restore-van"
                >
                  {archiveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Restore
                </Button>
              </DialogFooter>
            </div>
          ) : archiveCheck?.blocked && archiveCheck?.has_inventory ? (
            // Blocked - has inventory
            <div className="space-y-4">
              <div className="p-4 bg-destructive/10 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">Cannot Archive Vehicle</p>
                    <p className="text-sm text-destructive/80 mt-1">{archiveCheck.reason}</p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeArchiveDialog}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : archiveCheck?.blocked && archiveCheck?.assigned_technicians?.length > 0 ? (
            // Has assigned technicians - need to handle
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Users Assigned to This Vehicle</p>
                    <p className="text-sm text-amber-700 mt-1">
                      {archiveCheck.assigned_technicians.length} user(s) are currently assigned:
                    </p>
                    <ul className="mt-2 space-y-1">
                      {archiveCheck.assigned_technicians.map((tech) => (
                        <li key={tech.id} className="text-sm text-amber-700">
                          • {tech.name} ({tech.role})
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-bold uppercase tracking-wide">
                  What would you like to do with these users?
                </Label>
                <SearchableSelect
                  options={[
                    { value: 'unassign_all', label: 'Unassign all users' },
                    { value: 'reassign', label: 'Reassign to another vehicle' }
                  ]}
                  value={techAction}
                  onValueChange={setTechAction}
                  placeholder="Select action"
                  className="h-12"
                  sortAlphabetically={false}
                />
                
                {techAction === 'reassign' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-bold uppercase tracking-wide">
                      Target Vehicle *
                    </Label>
                    <SearchableSelect
                      options={activeVansForReassign.length === 0 
                        ? [{ value: 'none', label: 'No other vehicles available' }]
                        : activeVansForReassign.map((v) => ({ value: v.id, label: v.name }))
                      }
                      value={targetVehicleId}
                      onValueChange={setTargetVehicleId}
                      placeholder="Select a vehicle"
                      className="h-12"
                      emptyText="No vehicles available."
                    />
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={closeArchiveDialog} disabled={archiveLoading}>
                  Cancel
                </Button>
                <Button 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={handleArchive}
                  disabled={archiveLoading || (techAction === 'reassign' && !targetVehicleId)}
                  data-testid="confirm-archive-van"
                >
                  {archiveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Archive
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // No blockers - simple confirmation
            <div className="space-y-4">
              <DialogDescription>
                Are you sure you want to archive &quot;{archiveTarget?.van?.name}&quot;? It will be hidden from the active fleet but can be restored later.
              </DialogDescription>
              <DialogFooter>
                <Button variant="outline" onClick={closeArchiveDialog} disabled={archiveLoading}>
                  Cancel
                </Button>
                <Button 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={handleArchive}
                  disabled={archiveLoading}
                  data-testid="confirm-archive-van"
                >
                  {archiveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Archive
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
